import { CheckmarkFilled, Close } from '@carbon/icons-react';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { clsx, colorSet } from '@components/Provider';
import {
  Dialog,
  Drawer,
  IconButton,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { FC, PropsWithChildren } from 'react';
import { CommonProps, ContentsItemProps } from './types';

const ContentsContainer: FC<PropsWithChildren<{ onClose: () => void }>> = ({
  onClose,
  children
}) => {
  const classes = useStyles({ gutterBottom: undefined });

  return (
    <>
      <div className={classes.header}>
        <span className="title">정확한 견적을 받는 법</span>
        <IconButton className="close" onClick={onClose}>
          <Close size={24} />
        </IconButton>
      </div>

      <div className={classes.contents}>
        <div className={classes.expert}>
          <CheckmarkFilled size={24} color={colorSet.primary500} />

          <span>전문가의 조언을 받아 작성하였어요!</span>
        </div>

        {children}
      </div>
    </>
  );
};
const CommonModalWrapper: FC<PropsWithChildren<CommonProps>> = ({
  children,
  open,
  onClose
}) => {
  const matches = useMediaQuery(useTheme().breakpoints.down('xs'));
  const classes = useStyles({ gutterBottom: undefined });

  if (matches) {
    return (
      <Drawer anchor="bottom" open={open} classes={{ paper: classes.root }}>
        <ContentsContainer onClose={onClose}>{children}</ContentsContainer>
      </Drawer>
    );
  }

  return (
    <Dialog
      fullWidth
      classes={{ paper: classes.root }}
      open={open}
      onClose={onClose}
    >
      <ContentsContainer onClose={onClose}>{children}</ContentsContainer>

      <div className={classes.footer}>
        <CustomizedButton
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={onClose}
        >
          확인
        </CustomizedButton>
      </div>
    </Dialog>
  );
};

const ContentsItem: FC<PropsWithChildren<ContentsItemProps>> = ({
  title,
  children,
  examples,
  gutterBottom = 32
}) => {
  const classes = useStyles({ gutterBottom });

  return (
    <div
      className={clsx(classes.item, {
        gutterBottom: Boolean(gutterBottom)
      })}
    >
      <CustomizedTypography
        className="text"
        variant="h4"
        color="textPrimary"
        fontWeight="500"
        gutterBottoms={2}
      >
        {title}
      </CustomizedTypography>

      {typeof children === 'object' ? (
        children
      ) : (
        <CustomizedTypography
          className="text"
          color="textPrimary"
          variant="body1"
          gutterBottoms={examples ? 16 : 0}
        >
          {children}
        </CustomizedTypography>
      )}

      {examples && (
        <div className={classes.exampleBox}>
          <CustomizedTypography
            variant="body1"
            color="textSecondary"
            gutterBottoms={4}
          >
            예시
          </CustomizedTypography>

          {examples.map((example, index) => (
            <CustomizedTypography key={index}>
              {`${index + 1}. ${example}`}
            </CustomizedTypography>
          ))}
        </div>
      )}
    </div>
  );
};

export { CommonModalWrapper, ContentsItem };

const useStyles = makeStyles<Theme, { gutterBottom?: number }>(
  (theme) => ({
    root: {
      maxWidth: 476,
      maxHeight: 660,
      borderRadius: 16,
      [theme.breakpoints.down('xs')]: {
        maxWidth: '100%',
        maxHeight: 'unset',
        height: 'calc(100vh - 160px)',
        borderRadius: '16px 16px 0px 0px'
      }
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 24px',
      borderBottom: `1px solid ${colorSet.gray400}`,
      '& .title': {
        ...theme.typography.h4,
        fontWeight: 'bold'
      },
      '& .close': {
        padding: 6,
        '& svg': {
          color: colorSet.gray900
        }
      }
    },
    contents: {
      overflow: 'scroll',
      height: '100%',
      maxHeight: 700,
      padding: '20px 24px',
      [theme.breakpoints.down('xs')]: {
        padding: '20px 26px'
      }
    },
    item: {
      '& .text': {
        wordBreak: 'break-all'
      },
      '&.gutterBottom': {
        marginBottom: ({ gutterBottom }) => (gutterBottom ? gutterBottom : 0)
      },
      [theme.breakpoints.down('xs')]: {
        padding: '0px 12px'
      }
    },
    exampleBox: {
      backgroundColor: colorSet.gray100,
      padding: '16px 20px',
      borderRadius: '12px'
    },
    expert: {
      display: 'flex',
      alignItems: 'center',
      borderRadius: 12,
      padding: 16,
      backgroundColor: colorSet.primary100,
      marginBottom: 20,
      '& svg': {
        marginRight: 8
      },
      '& span': {
        ...theme.typography.body1,
        color: colorSet.primary500,
        fontWeight: '500'
      }
    },
    footer: {
      bottom: 0,
      position: 'sticky',
      width: '100%',
      padding: '16px 24px',
      borderTop: `1px solid ${colorSet.gray400}`,
      '& button': {
        '& span': {
          ...theme.typography.body1
        }
      }
    }
  }),
  {
    classNamePrefix: 'how-to-receive'
  }
);
